import { Trans } from "@lingui/react/macro";
import React, { useState } from "react";
import { Button, ConfigProvider, Dropdown, Menu, MenuProps, theme } from "antd";
import { Link, useParams } from "react-router";
import Icon, { DownOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { MenuItemType } from "antd/lib/menu/interface";
import clsx from "clsx";

import EnitBrandIcon from "../../../../styles/logo/enit-brand-signal.svg";
import colors from "../../../../styles/colors";

export function ApplicationSwitcher({
  collapsed,
  selectedApplication,
  items,
}: {
  collapsed: boolean;
  selectedApplication: string;
  items: Array<MenuItemType>;
}) {
  return (
    <ConfigProvider
      theme={{
        algorithm: theme.darkAlgorithm,
        token: { colorBgContainer: colors.aqua["900"] },
        components: {
          Menu: {
            popupBg: colors.aqua["900"],
            itemBg: colors.aqua["900"],
          },
          Dropdown: { colorBgElevated: colors.aqua["900"] },
        },
      }}
    >
      <div className="h-[var(--titlebar-height)] bg-[var(--topbar-color)] flex flex-col justify-center">
        <Menu
          mode={"inline"}
          inlineCollapsed={collapsed}
          selectedKeys={[]}
          items={[
            {
              key: "application",
              icon: <Icon component={EnitBrandIcon} />,
              label: (
                <Dropdown menu={{ items }} trigger={["click"]}>
                  <div
                    className="text-white flex flex-row gap-2"
                    onClick={(e) => e.preventDefault()}
                  >
                    <span className="pointer-events-none">
                      {items.find((i) => i.key === selectedApplication)?.label}
                    </span>
                    <DownOutlined style={{ fontSize: "10px" }} />
                  </div>
                </Dropdown>
              ),
              children: collapsed ? items : undefined,
            },
          ]}
        />
      </div>
    </ConfigProvider>
  );
}

export function Sidebar({
  navigationItems,
  selectedNavigationKey,
  applicationItems,
  selectedApplicationKey,
}: {
  navigationItems: MenuProps["items"];
  selectedNavigationKey: string;
  applicationItems: Array<MenuItemType>;
  selectedApplicationKey: string;
}) {
  const [collapsed, setCollapsed] = useState(false);

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  function findAllKeys(items: MenuProps["items"]): Array<string> {
    const result =
      items?.filter((item) => item != null && "key" in item).map((item) => item!.key! as string) ??
      [];
    items?.forEach((item) => {
      result.push(...findAllKeys(item != null && "children" in item ? item.children : []));
    });
    return result;
  }

  const allKeys = findAllKeys(navigationItems);

  return (
    <div
      style={{ transition: "width 0.3s cubic-bezier(0.2, 0, 0, 1) 0s" }}
      className={clsx(
        "row-span-full flex flex-col justify-between max-w-[var(--sidebar-width)] bg-white overflow-hidden",
        {
          "w-[var(--sidebar-width)]": !collapsed,
          "w-[4rem]": collapsed,
        }
      )}
    >
      <nav aria-label="Main applications">
        <ApplicationSwitcher
          collapsed={collapsed}
          selectedApplication={selectedApplicationKey}
          items={applicationItems}
        />
      </nav>
      <nav className="grow overflow-y-auto" aria-label="Application navigation">
        <Menu
          mode="inline"
          inlineCollapsed={collapsed}
          items={navigationItems}
          selectedKeys={[selectedNavigationKey]}
          defaultOpenKeys={allKeys}
        />
      </nav>
      <Button className="w-16" type="text" onClick={toggleCollapsed}>
        {collapsed ? (
          <MenuUnfoldOutlined aria-label="Expand menu" />
        ) : (
          <MenuFoldOutlined aria-label="Collapse menu" />
        )}
      </Button>
    </div>
  );
}

export function useApplicationItems() {
  const orgaId = useParams().orgaId ?? "app";

  return [
    {
      key: "ems",
      label: (
        <Link to={`/${orgaId}/energy-overview`}>
          <Trans>Energy Management</Trans>
        </Link>
      ),
    },
    {
      key: "co2",
      label: (
        <Link to={`/${orgaId}/carbon`}>
          <Trans>CO₂ Management</Trans>
        </Link>
      ),
    },
  ];
}
